import axios from 'axios'
import { useEffect, useState, useContext } from 'react'
import { customerType, salesOrderType } from '../types'
import { Link as LinkRouter, useNavigate } from 'react-router-dom'
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Container,
  TextField,
  Typography,
  Autocomplete,
  MenuItem,
  Grid,
  Link as LinkMui,
  TablePagination,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  IconButton,
  Chip,
  Box,
  TableSortLabel,
  Drawer,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItemText,
  ListItem,
  ListItemIcon,
  Checkbox,
  Divider,
  DialogActions,
  Alert,
  AlertTitle,
  Snackbar,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import './SalesOrderList.css'
import { DatePicker, getRepCodeName, OpsOwner } from '../helper'
import dayjs from 'dayjs'
import { salesOrderStatuses } from '../lists'
import IosShareIcon from '@mui/icons-material/IosShare'
import NoteAltOutlinedIcon from '@mui/icons-material/NoteAltOutlined'
import { ErrorOutline } from '@mui/icons-material'
import { dataContext } from '..'
import SalesOrderNotes from './SalesOrderNotes'
import { Close as CloseIcon } from '@mui/icons-material'
import ErrorScreen from '../Misc/ErrorScreen'

function SalesOrderList() {
  const navigate = useNavigate()
  const { user, operations, salesReps, customers } = useContext(dataContext)

  const [soList, setSoList] = useState<
    (salesOrderType & { customer_name: string; parent_name: string; statuses: string[] })[]
  >([])
  const [soCount, setSoCount] = useState<number>(0)
  const [filters, setFilters] = useState<{ [index: string]: string | string[] | number | undefined }>({
    soNumber: '',
    poNumber: '',
    customerPo: '',
    salesRep: '',
    customerName: '',
    jobName: '',
    dpasRating: '',
    salesOrderStatus: '',
    operationsOwner: '',
    orderStatusPrecise: [],
    salesOrderDateStart: '',
    salesOrderDateEnd: '',
    dueDateStart: '',
    dueDateEnd: '',
    quoteNumber: '',
    serialNumber: '',
    limit: 25,
    page: 0,
    sort: '',
  })
  const [jobNames, setJobNames] = useState<customerType[]>([])
  const [filteredJobNames, setFilteredJobNames] = useState<{ label: string; id: string }[]>([])
  const [exportDialogOpen, setExportDialogOpen] = useState(false)
  const [selectedReports, setSelectedReports] = useState<string[]>([])
  const [listLoading, setListLoading] = useState(true)
  const [initialLoad, setInitialLoad] = useState(true)
  const [notesOpen, setNotesOpen] = useState(false)
  const [notesId, setNotesId] = useState<string | null>(null)
  const [noteDraft, setNoteDraft] = useState('')
  const [previousNotesId, setPreviousNotesId] = useState<string | null>(null)
  const [exportError, setExportError] = useState(false)
  const [opsOwnerError, setOpsOwnerError] = useState<{ show: boolean; refNumber: string }>({
    show: false,
    refNumber: '',
  })
  const [exportInProgress, setExportInProgress] = useState(false)
  const [searchError, setSearchError] = useState(false)
  const [availableReports, setAvailableReports] = useState([
    {
      id: 'filteredList',
      label: 'Filtered Sales Order List Displayed on Page',
    },
  ])

  useEffect(() => {
    const jobNames: customerType[] = []
    customers.forEach(customer => {
      if (customer.parent_list_id) {
        jobNames.push(customer)
      }
    })

    setJobNames(jobNames)
  }, [customers])

  useEffect(() => {
    if (user.active_flag) {
      handleSearch(filters)
    }

    if (user.user_type !== 'Sales Rep') {
      setAvailableReports([
        {
          id: 'filteredList',
          label: 'Filtered Sales Order List Displayed on Page',
        },
        {
          id: 'openOrder',
          label: 'Open Order/Margin Report',
        },
        {
          id: 'latestNotes',
          label: 'SO Latest Notes Report',
        },
        {
          id: 'etaLatestNotes',
          label: 'ETA Latest Notes Report',
        },
      ])
    }
  }, [user])

  useEffect(() => {
    if (soList.length === 1) {
      navigate(`/salesOrder/${soList[0].sales_order_txn_id}`)
    }
  }, [soList])

  useEffect(() => {
    if (filters.customerName) {
      // Filter job names to only show those belonging to the selected customer
      const filtered = Array.from(jobNames.values())
        .filter(job => job.parent_list_id === filters.customerName)
        .map(({ customer_list_id, name }) => ({ id: customer_list_id, label: name }))
        .sort((a, b) => a.label.localeCompare(b.label))
      setFilteredJobNames(filtered)
    } else {
      // Show all job names when no customer is selected
      setFilteredJobNames(
        Array.from(jobNames.values())
          .map(({ customer_list_id, name }) => ({
            id: customer_list_id,
            label: name,
          }))
          .sort((a, b) => a.label.localeCompare(b.label)),
      )
    }
  }, [filters.customerName, jobNames])

  const handleSearch = (filters: { [index: string]: string | string[] | number | undefined }) => {
    let searchEndpoint = '/listSalesOrders?'
    for (const key of Object.keys(filters)) {
      if (filters[key]) {
        searchEndpoint += `${key}=${filters[key]}&`
      }
    }

    if (user.user_type === 'Sales Rep') {
      // backend type
      searchEndpoint += `salesUser=${user.user_id}&`
    }

    let loading = true
    setTimeout(() => {
      setListLoading(loading)
    }, 200)

    axios
      .get(searchEndpoint)
      .then(res => {
        setSoList(res.data.data)
        setSoCount(res.data.count)
        setSearchError(false)
      })
      .catch(err => {
        console.error(err)
        setSearchError(true)
      })
      .finally(() => {
        loading = false
        setListLoading(false)
        setInitialLoad(false)
      })
  }

  const handleClear = () => {
    const newFilters = {
      soNumber: '',
      poNumber: '',
      customerPo: '',
      salesRep: '',
      customerName: '',
      jobName: '',
      dpasRating: '',
      salesOrderStatus: '',
      orderStatusPrecise: [],
      salesOrderDateStart: '',
      salesOrderDateEnd: '',
      dueDateEnd: '',
      quoteNumber: '',
      serialNumber: '',
      operationsOwner: '',
      limit: filters.limit as number,
      page: 0,
      sort: '',
    }

    setFilters(newFilters)
    handleSearch(newFilters)
  }

  const handleExport = (
    reports: string[],
    filters: { [index: string]: string | string[] | number | undefined } = {},
  ) => {
    let searchEndpoint = '/export/salesOrderList?'
    for (const key of Object.keys(filters)) {
      if (filters[key]) {
        searchEndpoint += `${key}=${filters[key]}&`
      }
    }
    if (user.user_type === 'Sales Rep') {
      searchEndpoint += `salesUser=${user.user_id}&`
    }
    searchEndpoint += `reports=${reports.join(',')}&email=${user.email}`
    axios
      .get(searchEndpoint)
      .then(() => {
        setExportInProgress(true)
      })
      .catch(err => {
        console.error(err)
        setExportError(true)
      })
  }

  const handleOpsOwner = (salesOrderTxnId: string, operationsOwner: string | null) => {
    // Find the sales order to get the ref number for error messages
    const salesOrder = soList.find(so => so.sales_order_txn_id === salesOrderTxnId)
    const refNumber = salesOrder?.ref_number || salesOrderTxnId

    axios
      .post(`/salesOrder/${salesOrderTxnId}/updateOperationsOwner`, {
        operationsOwner,
        userId: user.user_id,
      })
      .then(() => {
        setSoList(
          prev =>
            prev.map(so =>
              so.sales_order_txn_id === salesOrderTxnId ? { ...so, operations_owner: operationsOwner } : so,
            ) as (salesOrderType & {
              customer_name: string
              parent_name: string
              statuses: string[]
            })[],
        )
      })
      .catch(err => {
        console.error(err)
        setOpsOwnerError({ show: true, refNumber })
      })
  }

  return (
    <div className="sales-order-list">
      <Container
        className={`sales-order-filter-container ${listLoading && initialLoad ? 'loading' : ''}`}
        maxWidth={false}
      >
        {listLoading && initialLoad ? (
          <>
            <div className="sales-order-input-row">
              <Skeleton className="skeleton-label" />
              <div className="skeleton-input" />
              <Button
                size="large"
                variant="contained"
                type="submit"
                onClick={e => {
                  e.preventDefault()
                  handleSearch(filters)
                }}
                loading={listLoading}
              >
                Search
              </Button>
              <Button size="large" variant="outlined" onClick={handleClear} loading={listLoading}>
                Clear
              </Button>
            </div>
            <Accordion className="sales-order-advanced-search" defaultExpanded={user.user_type === 'Operation Staff'}>
              <AccordionSummary expandIcon={<ExpandMoreIcon className="sales-order-advanced-search-expand-icon" />}>
                <Skeleton className="skeleton-advanced-search" />
              </AccordionSummary>
            </Accordion>
          </>
        ) : (
          <form>
            <div className="sales-order-input-row">
              <Typography variant="body2">Sales Order Number:</Typography>
              <TextField
                className="sales-order-number-input"
                label="Sales Order No."
                size="small"
                value={filters.soNumber}
                onChange={e => {
                  setFilters({
                    ...filters,
                    soNumber: e.target.value,
                  })
                }}
              />
              <Button
                size="large"
                variant="contained"
                type="submit"
                onClick={e => {
                  e.preventDefault()
                  handleSearch(filters)
                }}
                loading={listLoading && initialLoad}
              >
                Search
              </Button>
              <Button size="large" variant="outlined" onClick={handleClear} loading={listLoading && initialLoad}>
                Clear
              </Button>
            </div>
            <Accordion className="sales-order-advanced-search" defaultExpanded={user.user_type === 'Operation Staff'}>
              <AccordionSummary expandIcon={<ExpandMoreIcon className="sales-order-advanced-search-expand-icon" />}>
                <Typography>Advanced Search</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={'17px'}>
                  <Grid size={1.5} className="sales-order-advanced-search-grid">
                    <Typography variant="body2">IMPRES PO:</Typography>
                  </Grid>
                  <Grid size={2.5} className="sales-order-advanced-search-grid">
                    <TextField
                      variant="outlined"
                      size="small"
                      label="IMPRES PO"
                      value={filters.poNumber}
                      fullWidth
                      onChange={e => {
                        setFilters({
                          ...filters,
                          poNumber: e.target.value,
                        })
                      }}
                    />
                  </Grid>
                  <Grid size={1.5} className="sales-order-advanced-search-grid">
                    <Typography variant="body2">Customer Name:</Typography>
                  </Grid>
                  <Grid size={2.5} className="sales-order-advanced-search-grid">
                    <Autocomplete
                      autoHighlight
                      options={Array.from(customers.values())
                        .filter(customer => !customer.parent_list_id)
                        .map(({ customer_list_id, name }) => ({
                          id: customer_list_id,
                          label: name,
                        }))}
                      value={
                        filters.customerName
                          ? {
                              id: filters.customerName,
                              label:
                                customers.find(customer => customer.customer_list_id === filters.customerName)?.name ??
                                '',
                            }
                          : null
                      }
                      renderInput={params => <TextField {...params} size="small" label="Customer Name" />}
                      renderOption={(props, option) => (
                        <li {...props} key={option.id as string}>
                          {option.label}
                        </li>
                      )}
                      fullWidth
                      onChange={(_e, value) => {
                        setFilters({
                          ...filters,
                          customerName: value?.id ?? '',
                        })
                      }}
                    />
                  </Grid>
                  <Grid size={1.5} className="sales-order-advanced-search-grid">
                    <Typography variant="body2">Sales Order Date:</Typography>
                  </Grid>
                  <Grid size={2.5} className="sales-order-advanced-search-grid">
                    <div className="sales-order-advanced-search-date-group">
                      <DatePicker
                        label="From Date"
                        size="small"
                        value={filters.salesOrderDateStart ? dayjs.utc(filters.salesOrderDateStart as string) : null}
                        onChange={e => {
                          setFilters({
                            ...filters,
                            salesOrderDateStart: e?.format('YYYY-MM-DD') ?? '',
                          })
                        }}
                      />
                      <DatePicker
                        label="To Date"
                        size="small"
                        value={filters.salesOrderDateEnd ? dayjs.utc(filters.salesOrderDateEnd as string) : null}
                        onChange={e => {
                          setFilters({
                            ...filters,
                            salesOrderDateEnd: e?.format('YYYY-MM-DD') ?? '',
                          })
                        }}
                      />
                    </div>
                  </Grid>
                  <Grid size={1.5} className="sales-order-advanced-search-grid">
                    <Typography variant="body2">Operations Owner:</Typography>
                  </Grid>
                  <Grid size={2.5} className="sales-order-advanced-search-grid">
                    <Autocomplete
                      autoHighlight
                      options={[{ user_id: 'unassigned', display_name: 'Unassigned' }, ...operations]}
                      value={
                        filters.operationsOwner === 'unassigned'
                          ? { user_id: 'unassigned', display_name: 'Unassigned' }
                          : operations.find(user => user.user_id === filters.operationsOwner) ?? null
                      }
                      getOptionLabel={option => option.display_name || ''}
                      renderInput={params => <TextField {...params} size="small" label="Operations Owner" />}
                      fullWidth
                      onChange={(_e, value) => {
                        setFilters({
                          ...filters,
                          operationsOwner: value?.user_id === 'unassigned' ? 'unassigned' : value?.user_id ?? '',
                        })
                      }}
                    />
                  </Grid>
                  <Grid size={1.5} className="sales-order-advanced-search-grid">
                    <Typography variant="body2">Job Name:</Typography>
                  </Grid>
                  <Grid size={2.5} className="sales-order-advanced-search-grid">
                    <Autocomplete
                      autoHighlight
                      options={filteredJobNames}
                      value={filteredJobNames.find(job => job.id === filters.jobName) ?? null}
                      renderInput={params => <TextField {...params} size="small" label="Job Name" />}
                      renderOption={(props, option) => (
                        <li {...props} key={option.id as string}>
                          {option.label}
                        </li>
                      )}
                      fullWidth
                      onChange={(_e, value) => {
                        setFilters({
                          ...filters,
                          jobName: value?.id ?? '',
                        })
                      }}
                    />
                  </Grid>
                  <Grid size={1.5} className="sales-order-advanced-search-grid">
                    <Typography variant="body2">Due Date:</Typography>
                  </Grid>
                  <Grid size={2.5} className="sales-order-advanced-search-grid">
                    <div className="sales-order-advanced-search-date-group">
                      <DatePicker
                        label="From Date"
                        size="small"
                        value={filters.dueDateStart ? dayjs.utc(filters.dueDateStart as string) : null}
                        onChange={e => {
                          setFilters({
                            ...filters,
                            dueDateStart: e?.format('YYYY-MM-DD') ?? '',
                          })
                        }}
                      />
                      <DatePicker
                        label="To Date"
                        size="small"
                        value={filters.dueDateEnd ? dayjs.utc(filters.dueDateEnd as string) : null}
                        onChange={e => {
                          setFilters({
                            ...filters,
                            dueDateEnd: e?.format('YYYY-MM-DD') ?? '',
                          })
                        }}
                      />
                    </div>
                  </Grid>
                  <Grid size={1.5} className="sales-order-advanced-search-grid">
                    <Typography variant="body2">Customer PO:</Typography>
                  </Grid>
                  <Grid size={2.5} className="sales-order-advanced-search-grid">
                    <TextField
                      variant="outlined"
                      size="small"
                      label="Customer PO"
                      value={filters.customerPo}
                      fullWidth
                      onChange={e => {
                        setFilters({
                          ...filters,
                          customerPo: e.target.value,
                        })
                      }}
                    />
                  </Grid>
                  <Grid size={1.5} className="sales-order-advanced-search-grid">
                    <Typography variant="body2">Sales Order Status:</Typography>
                  </Grid>
                  <Grid size={2.5} className="sales-order-advanced-search-grid">
                    <TextField
                      size="small"
                      label="Sales Order Status"
                      select
                      value={filters.salesOrderStatus}
                      fullWidth
                      onChange={e => {
                        setFilters({
                          ...filters,
                          salesOrderStatus: e.target.value as string,
                        })
                      }}
                    >
                      <MenuItem value={'open'}>Open</MenuItem>
                      <MenuItem value={'closed'}>Closed</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid size={1.5} className="sales-order-advanced-search-grid">
                    <Typography variant="body2">Quote Number:</Typography>
                  </Grid>
                  <Grid size={2.5} className="sales-order-advanced-search-grid">
                    <TextField
                      variant="outlined"
                      size="small"
                      label="Quote Number"
                      value={filters.quoteNumber}
                      fullWidth
                      onChange={e => {
                        setFilters({
                          ...filters,
                          quoteNumber: e.target.value,
                        })
                      }}
                    />
                  </Grid>
                  <Grid size={1.5} className="sales-order-advanced-search-grid">
                    <Typography variant="body2">Sales Rep:</Typography>
                  </Grid>
                  <Grid size={2.5} className="sales-order-advanced-search-grid">
                    <Autocomplete
                      autoHighlight
                      options={salesReps}
                      value={salesReps.find(rep => rep.initial === filters.salesRep) ?? null}
                      getOptionLabel={option => `${option.initial} ${getRepCodeName(option)}`}
                      renderInput={params => <TextField {...params} size="small" label="Sales Rep" />}
                      fullWidth
                      onChange={(_e, value) => {
                        setFilters({
                          ...filters,
                          salesRep: value?.initial ?? '',
                        })
                      }}
                    />
                  </Grid>
                  <Grid size={1.5} className="sales-order-advanced-search-grid">
                    <Typography variant="body2">Order Status (Precise):</Typography>
                  </Grid>
                  <Grid size={2.5} className="sales-order-advanced-search-grid">
                    <TextField
                      size="small"
                      label="Order Status (Precise)"
                      select
                      slotProps={{
                        select: {
                          multiple: true,
                        },
                      }}
                      value={filters.orderStatusPrecise}
                      fullWidth
                      onChange={e => {
                        setFilters({
                          ...filters,
                          orderStatusPrecise: e.target.value,
                        })
                      }}
                    >
                      {salesOrderStatuses.map(status => (
                        <MenuItem value={status.id} key={status.id}>
                          {status.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid size={1.5} className="sales-order-advanced-search-grid">
                    <Typography variant="body2">DPAS Rating:</Typography>
                  </Grid>
                  <Grid size={2.5} className="sales-order-advanced-search-grid">
                    <TextField
                      size="small"
                      label="DPAS Rating"
                      select
                      value={filters.dpasRating}
                      fullWidth
                      onChange={e => {
                        setFilters({
                          ...filters,
                          dpasRating: e.target.value as string,
                        })
                      }}
                    >
                      <MenuItem value={'true'}>Available</MenuItem>
                      <MenuItem value={'false'}>Not Available</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid size={1.5} className="sales-order-advanced-search-grid">
                    <Typography variant="body2">Serial Number:</Typography>
                  </Grid>
                  <Grid size={2.5} className="sales-order-advanced-search-grid">
                    <TextField
                      variant="outlined"
                      size="small"
                      label="Serial Number"
                      value={filters.serialNumber}
                      fullWidth
                      onChange={e => {
                        setFilters({
                          ...filters,
                          serialNumber: e.target.value,
                        })
                      }}
                    />
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
          </form>
        )}
      </Container>

      {searchError ? (
        <div className="sales-order-list-error-container">
          <ErrorScreen
            errorText="Something went wrong while searching. Please try again or contact support if the error persists."
            title="Search Failed"
          />
        </div>
      ) : (
        <TableContainer className="sales-order-list-table">
          <div className="sales-order-pagination-header">
            <div className="sales-order-list-title">
              {initialLoad && listLoading ? (
                <Skeleton width={120} height={32} />
              ) : (
                <Typography variant="h6">Sales Orders</Typography>
              )}
              <Button
                variant="contained"
                startIcon={<IosShareIcon />}
                onClick={() => {
                  setExportDialogOpen(true)
                }}
                loading={initialLoad && listLoading}
              >
                Export Reports
              </Button>
              <Dialog
                open={exportDialogOpen}
                onClose={() => {
                  setExportDialogOpen(false)
                  setSelectedReports([])
                }}
                maxWidth="md"
                closeAfterTransition={false}
                fullWidth
              >
                <DialogTitle>Export Reports</DialogTitle>
                <DialogContent>
                  <Typography>Select the reports to export.</Typography>
                  <List className="sales-order-export-list">
                    <ListItem key="all">
                      <ListItemIcon>
                        <Checkbox
                          checked={selectedReports.length === availableReports.length}
                          onChange={(_, checked) => {
                            if (checked) {
                              setSelectedReports(availableReports.map(report => report.id))
                            } else {
                              setSelectedReports([])
                            }
                          }}
                        />
                      </ListItemIcon>
                      <ListItemText primary={`Select All (${availableReports.length})`} />
                    </ListItem>
                    <Divider />
                    {availableReports.map(report => (
                      <ListItem key={report.id}>
                        <ListItemIcon>
                          <Checkbox
                            checked={selectedReports.includes(report.id)}
                            onChange={(_, checked) => {
                              if (checked) {
                                setSelectedReports(prev => [...prev, report.id])
                              } else {
                                setSelectedReports(prev => prev.filter(r => r !== report.id))
                              }
                            }}
                          />
                        </ListItemIcon>
                        <ListItemText primary={report.label} />
                      </ListItem>
                    ))}
                  </List>
                </DialogContent>
                <DialogActions>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setExportDialogOpen(false)
                      setSelectedReports([])
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    onClick={() => {
                      setExportDialogOpen(false)
                      setSelectedReports([])
                      handleExport(selectedReports, filters)
                    }}
                    disabled={selectedReports.length === 0}
                  >
                    Export
                  </Button>
                </DialogActions>
              </Dialog>
            </div>
            {initialLoad && listLoading ? (
              <Skeleton width={300} height={32} />
            ) : (
              <TablePagination
                component="div"
                count={soCount}
                page={filters.page as number}
                rowsPerPage={filters.limit as number}
                onPageChange={(_e, newPage) => {
                  const newFilters = { ...filters, page: newPage }
                  setFilters(newFilters)
                  handleSearch(newFilters)
                }}
                onRowsPerPageChange={e => {
                  const newFilters = { ...filters, limit: parseInt(e.target.value) }
                  setFilters(newFilters)
                  handleSearch(newFilters)
                }}
              />
            )}
          </div>
          <Table stickyHeader color="primary">
            <TableHead>
              {initialLoad && listLoading ? (
                <TableRow>
                  {Array.from({ length: 15 }).map((_, index) => (
                    <TableCell key={index}>
                      <Skeleton className="skeleton-header" />
                    </TableCell>
                  ))}
                </TableRow>
              ) : (
                <TableRow>
                  <TableCell width="4%">
                    <TableSortLabel
                      active={(filters.sort as string).includes('ref_number')}
                      direction={filters.sort === 'ref_number' ? 'asc' : 'desc'}
                      onClick={() => {
                        const newFilters = {
                          ...filters,
                          sort: filters.sort === 'ref_number' ? 'ref_number desc' : 'ref_number',
                        }
                        setFilters(newFilters)
                        handleSearch(newFilters)
                      }}
                    >
                      SO #
                    </TableSortLabel>
                  </TableCell>
                  <TableCell width="7%">
                    <TableSortLabel
                      active={(filters.sort as string).includes('operations_owner')}
                      direction={filters.sort === 'operations_owner' ? 'asc' : 'desc'}
                      onClick={() => {
                        const newFilters = {
                          ...filters,
                          sort: filters.sort === 'operations_owner' ? 'operations_owner desc' : 'operations_owner',
                        }
                        setFilters(newFilters)
                        handleSearch(newFilters)
                      }}
                    >
                      Operations Owner
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={(filters.sort as string).includes('quote_works_number')}
                      direction={filters.sort === 'quote_works_number' ? 'asc' : 'desc'}
                      onClick={() => {
                        const newFilters = {
                          ...filters,
                          sort:
                            filters.sort === 'quote_works_number' ? 'quote_works_number desc' : 'quote_works_number',
                        }
                        setFilters(newFilters)
                        handleSearch(newFilters)
                      }}
                    >
                      Quote #
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={(filters.sort as string).includes('parent_name')}
                      direction={filters.sort === 'parent_name' ? 'asc' : 'desc'}
                      onClick={() => {
                        const newFilters = {
                          ...filters,
                          sort: filters.sort === 'parent_name' ? 'parent_name desc' : 'parent_name',
                        }
                        setFilters(newFilters)
                        handleSearch(newFilters)
                      }}
                    >
                      Customer Name
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={(filters.sort as string).includes('customer_name')}
                      direction={filters.sort === 'customer_name' ? 'asc' : 'desc'}
                      onClick={() => {
                        const newFilters = {
                          ...filters,
                          sort: filters.sort === 'customer_name' ? 'customer_name desc' : 'customer_name',
                        }
                        setFilters(newFilters)
                        handleSearch(newFilters)
                      }}
                    >
                      Job Name
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={(filters.sort as string).includes('rep_code')}
                      direction={filters.sort === 'rep_code' ? 'asc' : 'desc'}
                      onClick={() => {
                        const newFilters = {
                          ...filters,
                          sort: filters.sort === 'rep_code' ? 'rep_code desc' : 'rep_code',
                        }
                        setFilters(newFilters)
                        handleSearch(newFilters)
                      }}
                    >
                      Sales Rep
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right" className="date-cell">
                    <TableSortLabel
                      active={(filters.sort as string).includes('txn_date')}
                      direction={filters.sort === 'txn_date' ? 'asc' : 'desc'}
                      onClick={() => {
                        const newFilters = {
                          ...filters,
                          sort: filters.sort === 'txn_date' ? 'txn_date desc' : 'txn_date',
                        }
                        setFilters(newFilters)
                        handleSearch(newFilters)
                      }}
                    >
                      SO Date
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right" width="5%" className="date-cell">
                    <TableSortLabel
                      active={(filters.sort as string).includes('due_date')}
                      direction={filters.sort === 'due_date' ? 'asc' : 'desc'}
                      onClick={() => {
                        const newFilters = {
                          ...filters,
                          sort: filters.sort === 'due_date' ? 'due_date desc' : 'due_date',
                        }
                        setFilters(newFilters)
                        handleSearch(newFilters)
                      }}
                    >
                      Due Date
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={(filters.sort as string).includes('dpass')}
                      direction={filters.sort === 'dpass' ? 'asc' : 'desc'}
                      onClick={() => {
                        const newFilters = {
                          ...filters,
                          sort: filters.sort === 'dpass' ? 'dpass desc' : 'dpass',
                        }
                        setFilters(newFilters)
                        handleSearch(newFilters)
                      }}
                    >
                      DPAS
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right" width="6%">
                    <TableSortLabel
                      active={(filters.sort as string).includes('total_amount')}
                      direction={filters.sort === 'total_amount' ? 'asc' : 'desc'}
                      onClick={() => {
                        const newFilters = {
                          ...filters,
                          sort: filters.sort === 'total_amount' ? 'total_amount desc' : 'total_amount',
                        }
                        setFilters(newFilters)
                        handleSearch(newFilters)
                      }}
                    >
                      Total Amount
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Order Status</TableCell>
                  <TableCell width="3%">
                    <TableSortLabel
                      active={(filters.sort as string).includes('is_fully_invoiced')}
                      direction={filters.sort === 'is_fully_invoiced' ? 'asc' : 'desc'}
                      onClick={() => {
                        const newFilters = {
                          ...filters,
                          sort: filters.sort === 'is_fully_invoiced' ? 'is_fully_invoiced desc' : 'is_fully_invoiced',
                        }
                        setFilters(newFilters)
                        handleSearch(newFilters)
                      }}
                    >
                      Invoiced
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right" width="4%">
                    <TableSortLabel
                      active={(filters.sort as string).includes('line_item_late_count')}
                      direction={filters.sort === 'line_item_late_count' ? 'asc' : 'desc'}
                      onClick={() => {
                        const newFilters = {
                          ...filters,
                          sort:
                            filters.sort === 'line_item_late_count'
                              ? 'line_item_late_count desc'
                              : 'line_item_late_count',
                        }
                        setFilters(newFilters)
                        handleSearch(newFilters)
                      }}
                    >
                      Late Item Ct.
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right" className="date-cell">
                    <TableSortLabel
                      active={(filters.sort as string).includes('last_update_date')}
                      direction={filters.sort === 'last_update_date' ? 'asc' : 'desc'}
                      onClick={() => {
                        const newFilters = {
                          ...filters,
                          sort: filters.sort === 'last_update_date' ? 'last_update_date desc' : 'last_update_date',
                        }
                        setFilters(newFilters)
                        handleSearch(newFilters)
                      }}
                    >
                      Last Modified
                    </TableSortLabel>
                  </TableCell>
                  <TableCell width="3%">Notes</TableCell>
                </TableRow>
              )}
            </TableHead>
            <TableBody>
              {listLoading && !initialLoad
                ? Array.from({ length: filters.limit as number }).map((_, rowIndex) => (
                    <TableRow key={rowIndex}>
                      {Array.from({ length: 15 }).map((_, colIndex) => (
                        <TableCell key={colIndex}>
                          <Skeleton />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                : soList.map(so => (
                    <TableRow key={so.sales_order_txn_id}>
                      <TableCell>
                        <LinkMui to={`/salesOrder/${so.sales_order_txn_id}`} component={LinkRouter}>
                          {so.ref_number}
                        </LinkMui>
                      </TableCell>
                      <TableCell>
                        <OpsOwner
                          operationsOwner={so.operations_owner}
                          objectId={so.sales_order_txn_id}
                          handleEditOpsOwner={handleOpsOwner}
                          key={so.sales_order_txn_id}
                        />
                      </TableCell>
                      <TableCell>{so.quote_works_number}</TableCell>
                      <TableCell>{so.parent_name}</TableCell>
                      <TableCell>{so.customer_name}</TableCell>
                      <TableCell>{`${so.rep_code} ${getRepCodeName(
                        salesReps.find(rep => rep.initial === so.rep_code),
                      )}`}</TableCell>
                      <TableCell align="right" className="date-cell">
                        {so.txn_date ? dayjs.utc(so.txn_date).format('MM/DD/YYYY').replace(/\//g, '/\u200B') : ''}
                      </TableCell>
                      <TableCell align="right" className="date-cell">
                        {so.due_date ? dayjs.utc(so.due_date).format('MM/DD/YYYY').replace(/\//g, '/\u200B') : ''}
                      </TableCell>
                      <TableCell>{so.dpass}</TableCell>
                      <TableCell align="right">
                        $
                        {so.total_amount?.toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </TableCell>
                      <TableCell>
                        <Box className="sales-order-list-status-column">
                          {so.statuses.map(status =>
                            status === 'Late' ? (
                              <Chip
                                size="small"
                                color={so.display_color_red ? 'error' : 'default'}
                                label={status}
                                icon={<ErrorOutline />}
                                key={so.sales_order_txn_id + ' ' + status}
                              />
                            ) : (
                              status
                            ),
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>{so.is_fully_invoiced ? 'Yes' : 'No'}</TableCell>
                      <TableCell align="right">{so.line_item_late_count}</TableCell>
                      <TableCell align="right" className="date-cell">
                        {so.last_update_date
                          ? dayjs.utc(so.last_update_date).format('MM/DD/YYYY').replace(/\//g, '/\u200B')
                          : ''}
                      </TableCell>
                      <TableCell>
                        <IconButton
                          onClick={() => {
                            // Only clear draft if switching to a different sales order
                            if (previousNotesId !== so.sales_order_txn_id) {
                              setNoteDraft('')
                            }
                            setPreviousNotesId(so.sales_order_txn_id)
                            setNotesId(so.sales_order_txn_id)
                            setNotesOpen(true)
                          }}
                        >
                          <NoteAltOutlinedIcon color="primary" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
            </TableBody>
          </Table>
          <TablePagination
            className="sales-order-pagination-footer"
            component="div"
            count={soCount}
            page={filters.page as number}
            rowsPerPage={filters.limit as number}
            onPageChange={(_e, newPage) => {
              const newFilters = { ...filters, page: newPage }
              setFilters(newFilters)
              handleSearch(newFilters)
            }}
            onRowsPerPageChange={e => {
              const newFilters = { ...filters, limit: parseInt(e.target.value) }
              setFilters(newFilters)
              handleSearch(newFilters)
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </TableContainer>
      )}
      <Drawer open={notesOpen} onClose={() => setNotesOpen(false)} anchor="right">
        <SalesOrderNotes
          salesOrderId={notesId}
          objectType="salesOrder"
          setNotesOpen={setNotesOpen}
          noteDraft={noteDraft}
          setNoteDraft={setNoteDraft}
        />
      </Drawer>
      <div className="sales-order-error-alerts-container">
        {exportError && (
          <Alert severity="error">
            <AlertTitle>Export Failed</AlertTitle>
            An error occurred. Please try again or contact support if the error persists.
          </Alert>
        )}
        {opsOwnerError.show && (
          <Alert severity="error">
            <AlertTitle>Operations Owner Not Saved</AlertTitle>
            An error occurred while saving the operations owner to SO #{opsOwnerError.refNumber}. Please try again or
            contact support if the error persists.
          </Alert>
        )}
        {exportInProgress && (
          <Snackbar
            open={exportInProgress}
            message="Your export is taking some time to complete. You will receive an email when it is ready."
            action={
              <IconButton size="small" color="inherit" onClick={() => setExportInProgress(false)}>
                <CloseIcon fontSize="small" />
              </IconButton>
            }
          />
        )}
      </div>
    </div>
  )
}

export default SalesOrderList
